import { pendingFilter, useInterval } from '@valiant/ui-shared'
import {
  useGetCompanyUtxoMempoolActivityQuery,
  useGetTransactionsQuery,
  type GetCompanyUtxoMempoolActivityQuery,
  type Transaction,
} from '../../generated/graphql'
import { PendingTransactionItem } from './shared/PendingTransactionItem'
import { UtxoMempoolActivityItem } from './shared/UtxoMempoolActivityItem'
import { SwapStatus, type DepositStatus, SkeletonPendingActivityList } from '@valiant/ui-shared'
import { useMemo } from 'react'
import { maskSwapStatus } from '../../utils/swapStatus'

interface PendingActivityProps {
  companyId: string
}

type UtxoMempoolActivity =
  GetCompanyUtxoMempoolActivityQuery['getCompanyUtxoMempoolActivity'][number]

export function PendingActivity({ companyId }: PendingActivityProps) {
  const [{ data: candidateData, fetching: candidatesLoading }, refetchCandidates] =
    useGetCompanyUtxoMempoolActivityQuery({
      variables: { companyId },
      pause: !companyId,
      requestPolicy: 'network-only',
    })

  const [{ data: transactionsData, fetching: transactionsLoading }, refetchTransactions] =
    useGetTransactionsQuery({
      variables: {
        filter: {
          companyId: companyId || '',
        },
      },
      pause: !companyId,
      requestPolicy: 'network-only',
    })

  // Auto-refetch transactions every 10 seconds to catch status changes
  useInterval(() => {
    if (companyId) {
      refetchTransactions({ requestPolicy: 'network-only' })
      refetchCandidates({ requestPolicy: 'network-only' })
    }
  }, 10000)

  const pendingTransactions = useMemo(() => {
    if (!transactionsData?.getTransactions.nodes) return []

    return transactionsData.getTransactions.nodes.filter((transaction) => {
      if (transaction.type === 'TRADE' && transaction.swapStatus === SwapStatus.Rejected) {
        return false
      }
      const status =
        transaction.depositStatus ||
        maskSwapStatus(transaction.swapStatus, transaction.isExternalApi) ||
        transaction.status
      return pendingFilter.includes(status as unknown as SwapStatus | DepositStatus)
    })
  }, [transactionsData])

  const allPendingActivities = useMemo(() => {
    const activities: Array<{
      id: string
      type: 'pending' | 'utxo-candidate'
      data: Transaction | UtxoMempoolActivity
      timestamp: number
    }> = []

    pendingTransactions.forEach((transaction) => {
      activities.push({
        id: transaction.id,
        type: 'pending',
        data: transaction,
        timestamp: new Date(transaction.createdDate || '').getTime(),
      })
    })

    candidateData?.getCompanyUtxoMempoolActivity.forEach((candidate) => {
      activities.push({
        id: candidate.id,
        type: 'utxo-candidate',
        data: candidate,
        timestamp: new Date(candidate.observedAt).getTime(),
      })
    })

    return activities.sort((a, b) => b.timestamp - a.timestamp)
  }, [candidateData, pendingTransactions])

  const isLoading =
    (candidatesLoading && candidateData === undefined) ||
    (transactionsLoading && transactionsData === undefined)

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Pending Activity</h2>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse"></div>
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
        <SkeletonPendingActivityList count={3} />
      </div>
    )
  }

  if (allPendingActivities.length === 0) {
    return null
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Pending Activity</h2>

      <div className="space-y-3">
        {allPendingActivities.map((activity) => {
          if (activity.type === 'pending') {
            return (
              <PendingTransactionItem
                key={activity.id}
                transaction={activity.data as Transaction}
              />
            )
          } else {
            return (
              <UtxoMempoolActivityItem
                key={activity.id}
                activity={activity.data as UtxoMempoolActivity}
              />
            )
          }
        })}
      </div>
    </div>
  )
}
